import { Url } from "@yuigoto/validators";

/**
 * core/utils/RequestObject
 * ----------------------------------------------------------------------
 * Fornece métodos para criação de objetos utilizados em requests HTTP pelo
 * `axios`.
 *
 * @since     0.0.1
 */

/**
 * Usada p/ validar métodos HTTP fornecidos.
 * 
 * @type {RegExp}
 */
const HTTP_METHOD_REGEX = /(POST|GET|PUT|DELETE|HEAD|CONNECT|OPTIONS|TRACE)/;

/**
 * Utilizada para matching de parâmetros de URL entre chaves.
 * 
 * @type {RegExp}
 */
const URL_PARAMS_REGEX = /(\{[a-z0-9]+\})/gi;

/**
 * Aplica os dados de `paramData` em `url`, de acordo com as chaves em 
 * `paramList`.
 * 
 * @param {string} url 
 *     URL para replace
 * @param {string[]} paramList 
 *     Lista contendo as chaves de replacement da URL
 * @param {object} paramData 
 *     Objeto contendo pares chave/valor para reposição
 * @returns {string} 
 */
const ApplyUrlParams = (url, paramList, paramData) => {
  if (!paramList || paramList.length === 0) return url;

  for (let param of paramList) {
    url = url.replace(
      new RegExp(`{${param}}`, "g"),
      paramData[param]
    );
  }

  return url;
};

/**
 * Extrai, de uma URL de endpoint, os parâmetros que o usuário deve declarar.
 * 
 * @param {string} url 
 *     URL para extração de parâmetros
 * @return {string[]}
 */
const GetUrlParams = (url) => {
  let params = url.match(URL_PARAMS_REGEX);

  if (params === null || params.length === 0) return null;
  for (let n = 0; n < params.length; n++) {
    params[n] = params[n].replace(/\{|\}/g, "");
  }
  return params;
};

/**
 * Verifica se todos os parâmetros obrigatórios encontram-se nos dados fornecidos.
 * 
 * @param {string[]} paramList 
 *     Array contendo os nomes de parâmetros obrigatórios
 * @param {object} paramData 
 *     Objeto contendo pares chave/valor relativos aos parâmetros de URL 
 * @returns {boolean}
 */
const HasUrlParams = (paramList, paramData) => {
  let paramKeys = Object.keys(paramData),
      declaredParams = paramList
        .filter(
          item => paramKeys.includes(item)
        );
  return (declaredParams.length === paramList.length);
};

/**
 * Verifica se o objeto de dados fornecido possui todos os campos obrigatórios 
 * declarados em `required` no endpoint (apenas quando presente).
 * 
 * @param {string[]} requiredList 
 *     Array contendo os nomes de todos os campos obrigatórios para o endpoint
 * @param {object} data 
 *     Objeto de dados para envio 
 * @returns {boolean}
 */
const HasRequiredData = (requiredList, data) => {
  if (!requiredList || requiredList.length === 0) return true;

  let dataKeys = Object.keys(data),
      requiredData = requiredList
        .filter(item => {
          if (/\|/g.test(item)) {
            let includes = false,
                _split = item.split("|");
            
            for (let key of _split) {
              if (
                key.trim() !== ""
                && dataKeys.includes(key)
              ) {
                includes = true;
              }
            }

            return includes;
          } else {
            return dataKeys.includes(item);
          }
        });
  
  return (requiredData.length === requiredList.length);
};

/**
 * Apenas valida o nome do método HTTP a ser utilizado.
 * 
 * @param {string} method 
 *     Método HTTP a ser utilizado
 * @returns {boolean}
 */
const IsValidHttpMethod = (method) => {
  return HTTP_METHOD_REGEX.test(method.trim());
};

/**
 * Monta um objeto para uso em requests com base no endpoint e dados fornecidos.
 * 
 * @param {*} endpoint 
 *     Objeto de endpoint, contendo URL, métodos aceitos e eventuais parâmetros 
 *     que sejam obrigatórios 
 * @param {object} data 
 *     Objeto contendo dados para envio, caso `endpoint` possua o parâmetro 
 *     `required` as chaves contidas neste objeto serão validadas com base nele 
 * @param {string} method 
 *     Método HTTP a ser utilizado para o request 
 * @param {object} headers 
 *     Objeto contendo pares chave/valor referentes aos headers de página 
 * @param {object} params 
 *     Objeto opcional, contendo pares chave/valor com parâmetros de URL, para 
 *     replacement direto na string, caso a string `url` em endpoint possua 
 *     algum parâmetro, declarar este parâmetro é obrigatório 
 * @returns {object|boolean} 
 */
export const CreateRequestObject = (
  endpoint, 
  data, 
  method, 
  headers, 
  params = null
) => {
  try {
    if (
      !endpoint.hasOwnProperty("url") 
      || !endpoint.hasOwnProperty("method") 
    ) {
      throw new TypeError(
        `Um endpoint deve ter, ao menos, os parâmetros 'url' e 'method'.`
      );
    }

    if (!IsValidHttpMethod(method)) {
      throw new TypeError(
        `O método HTTP fornecido é inválido.`
      );
    }
    let returnable = {
          method
        },
        url = endpoint.url,
        urlParams = GetUrlParams(url);
    
    if (headers) returnable.headers = headers;
    
    if (urlParams && params) {
      if (!HasUrlParams(urlParams, params)) {
        throw new TypeError(
          `Você não declarou parâmetros de URL obrigatórios.`
        );
      }
  
      url = ApplyUrlParams(url, urlParams, params);
    }

    if (endpoint.hasOwnProperty("required")) {
      if (!HasRequiredData(endpoint.required, data)) {
        throw new TypeError(
          `Campos obrigatórios não foram declarados.`
        );
      }
    }

    if (method === "GET") {
      returnable.params = data;
    } else {
      returnable.data = JSON.stringify(data);
    }

    returnable.mode = "no-cors";
  
    returnable.url = url;
  
    return returnable;
  } catch (e) {
    console.error(e);
    return false;
  }
};

/**
 * Monta um objeto para uso em requests diretamente em URLs.
 * 
 * @param {string} url 
 *     URL completa para onde o request será enviado 
 * @param {object} data 
 *     Objeto contendo dados para envio, caso `required` seja declarado, 
 *     as chaves contidas neste objeto serão validadas com base nele 
 * @param {string} method 
 *     Método HTTP a ser utilizado para o request 
 * @param {object} headers 
 *     Objeto contendo pares chave/valor referentes aos headers de página 
 * @param {string[]} required 
 *     Opcional, deve conter as chaves a ser consideradas como obrigatórias 
 *     dentro dos dados a serem enviados 
 * @returns {object|boolean} 
 */
export const CreateRequestUrlObject = (
  url,
  data,
  method,
  headers, 
  required = null 
) => {
  try {
    if (!Url.validate(url)) {
      throw new TypeError(
        `URL inválida.`
      );
    }

    if (!IsValidHttpMethod(method)) {
      throw new TypeError(
        `O método HTTP fornecido é inválido.`
      );
    }

    let returnable = {
      url
    };
    
    if (headers) returnable.headers = headers;

    if (required && !HasRequiredData(required, data)) {
      throw new TypeError(
        `Campos obrigatórios não foram declarados.`
      );
    }

    if (method === "GET") {
      returnable.params = data;
    } else {
      returnable.data = JSON.stringify(data);
    }

    returnable.mode = "no-cors";

    return returnable;
  } catch (e) {
    console.error(e);
    return false;
  }
}
